import { useCallback } from 'react';
import { trackEvent } from '@services/tracking';
import { useTogglePinPost } from '../api/useTogglePinPost';
import { CommunityId } from '../model/community.types';
import { MAX_PINNED_POSTS } from '../model/community.constants';
import { Post } from '../model/post.types';
import { resolveInfluencerId } from './resolveInfluencerId';

const PIN_LIMIT_COPY = 'Pin limit reached. Unpin a post to add a new one.';

export type PinPostAction = {
  checked: boolean;
  disabled: boolean;
  helperText?: string;
  toggle: () => void;
};

export const usePinPostAction = (
  post: Post,
  communityId: CommunityId,
  pinnedCount: number,
  influencerId?: string | number,
): PinPostAction => {
  const togglePin = useTogglePinPost(communityId);
  const blockedByLimit = !post.pinned && pinnedCount >= MAX_PINNED_POSTS;

  const toggle = useCallback(() => {
    const nextPinned = !post.pinned;
    togglePin(post.id, nextPinned);

    const id = resolveInfluencerId(influencerId);
    if (id !== null) {
      trackEvent({
        type: nextPinned ? 'Post pinned' : 'Post unpinned',
        payload: { 'Influencer ID': id },
      });
    }
  }, [post.pinned, post.id, togglePin, influencerId]);

  return {
    checked: post.pinned,
    disabled: blockedByLimit,
    helperText: blockedByLimit ? PIN_LIMIT_COPY : undefined,
    toggle,
  };
};
